import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';
import { toast } from 'sonner';
import { Users, Gift, Edit, CreditCard, Plus, Trash2, ArrowUp, ArrowDown, X, BarChart3 } from 'lucide-react';

export default function AdminPanel() {
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [creditPurchases, setCreditPurchases] = useState([]);
  const [showTenantDialog, setShowTenantDialog] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [tokenUsageData, setTokenUsageData] = useState(null);
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [creditPackages, setCreditPackages] = useState([]);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '',
    credits: '',
    price: '',
    popular: false,
    active: true
  });
  const [siteContents, setSiteContents] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [contentForm, setContentForm] = useState({});
  const [tenantForm, setTenantForm] = useState({
    plan_id: '',
    bonus_questions: '',
    status: ''
  });
  const [creditForm, setCreditForm] = useState({
    tenant_id: '',
    credits: '',
    is_bonus: true,
    note: ''
  });
  const [planForm, setPlanForm] = useState({
    name: '',
    price: '',
    currency: 'TRY',
    period: 'monthly',
    order: 0,
    active: true,
    features: {
      questions_per_month: '',
      ai_model: 'Merkur AI',
      support: 'Email Destek',
      auto_mode: true
    }
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tenantsRes, plansRes, creditsRes, packagesRes] = await Promise.all([
        api.get('/admin/tenants'),
        api.get('/admin/plans'),
        api.get('/admin/credits/purchases'),
        api.get('/admin/credit-packages')
      ]);
      setTenants(tenantsRes.data);
      setPlans(plansRes.data);
      setCreditPurchases(creditsRes.data);
      setCreditPackages(packagesRes.data);
      
      // Load site contents
      try {
        const contentsRes = await api.get('/content-management');
        console.log('✅ Admin site-content response:', contentsRes.data);
        
        if (Array.isArray(contentsRes.data)) {
          console.log(`✅ Loaded ${contentsRes.data.length} site contents`);
          contentsRes.data.forEach(c => {
            console.log(`  - ${c.page}: ${c.content ? Object.keys(c.content).length : 0} keys in content`);
          });
          setSiteContents(contentsRes.data);
        } else {
          console.error('❌ Response is not an array:', contentsRes.data);
          setSiteContents([]);
        }
      } catch (contentError) {
        console.error('❌ Site content load error:', contentError);
        console.error('Error details:', contentError.response?.data);
        setSiteContents([]);
      }
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
      console.error('Load data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditContent = (content) => {
    console.log('Editing content:', content);
    console.log('Content.content:', content.content);
    setSelectedPage(content.page);
    setContentForm(content.content || {});
  };

  const handleSaveContent = async () => {
    try {
      console.log('💾 Saving content for:', selectedPage);
      console.log('Content keys:', Object.keys(contentForm));
      
      await api.put(`/content-management/${selectedPage}`, {
        content: contentForm
      });
      
      toast.success('İçerik güncellendi');
      setSelectedPage(null);
      loadData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('İçerik güncellenirken hata oluştu: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleCreatePackage = () => {
    setSelectedPackage(null);
    setPackageForm({
      name: '',
      credits: '',
      price: '',
      popular: false,
      active: true
    });
    setShowPackageDialog(true);
  };

  const handleEditPackage = (pkg) => {
    setSelectedPackage(pkg);
    setPackageForm({
      name: pkg.name,
      credits: pkg.credits,
      price: pkg.price,
      popular: pkg.popular,
      active: pkg.active
    });
    setShowPackageDialog(true);
  };

  const handleSavePackage = async () => {
    try {
      if (selectedPackage) {
        // Update
        await api.put(`/admin/credit-packages/${selectedPackage.id}`, packageForm);
        toast.success('Paket güncellendi');
      } else {
        // Create
        await api.post('/admin/credit-packages', packageForm);
        toast.success('Paket oluşturuldu');
      }
      setShowPackageDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!confirm('Bu paketi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await api.delete(`/admin/credit-packages/${packageId}`);
      toast.success('Paket silindi');
      loadData();
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setTenantForm({
      plan_id: tenant.subscription?.plan_id || '',
      bonus_questions: tenant.subscription?.bonus_questions || 0,
      status: tenant.subscription?.status || 'active',
      credits: tenant.credits || 0,
      bonus_credits: tenant.bonus_credits || 0
    });
    setShowTenantDialog(true);
  };

  const handleUpdateTenant = async () => {
    try {
      // Update subscription status
      await api.put(`/admin/tenants/${selectedTenant.id}/plan`, {
        plan_id: tenantForm.plan_id || null,
        bonus_questions: parseInt(tenantForm.bonus_questions) || 0,
        status: tenantForm.status
      });

      // Update credits if changed
      const creditChange = parseInt(tenantForm.credits) - (selectedTenant.credits || 0);
      const bonusCreditChange = parseInt(tenantForm.bonus_credits) - (selectedTenant.bonus_credits || 0);
      
      if (creditChange !== 0) {
        await api.post('/admin/credits/set', {
          tenant_id: selectedTenant.id,
          credits: parseInt(tenantForm.credits),
          is_bonus: false
        });
      }
      
      if (bonusCreditChange !== 0) {
        await api.post('/admin/credits/set', {
          tenant_id: selectedTenant.id,
          credits: parseInt(tenantForm.bonus_credits),
          is_bonus: true
        });
      }
      
      toast.success('Tenant bilgileri güncellendi');
      setShowTenantDialog(false);
      setSelectedTenant(null);
      loadData();
    } catch (error) {
      toast.error('Güncelleme hatası: ' + (error.response?.data?.detail || error.message));
      console.error(error);
    }
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    setPlanForm({
      name: '',
      price: '',
      currency: 'TRY',
      period: 'monthly',
      order: plans.length,
      active: true,
      features: {
        questions_per_month: '',
        ai_model: 'Merkur AI',
        support: 'Email Destek',
        auto_mode: true
      }
    });
    setShowPlanDialog(true);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setPlanForm({
      name: plan.name,
      price: plan.price,
      currency: plan.currency,
      period: plan.period,
      order: plan.order || 0,
      active: plan.active !== false,
      features: plan.features
    });
    setShowPlanDialog(true);
  };

  const handleSavePlan = async () => {
    try {
      const planData = {
        ...planForm,
        price: parseFloat(planForm.price),
        features: {
          ...planForm.features,
          questions_per_month: parseInt(planForm.features.questions_per_month)
        }
      };

      if (selectedPlan) {
        await api.put(`/admin/plans/${selectedPlan.id}`, planData);
        toast.success('Plan güncellendi');
      } else {
        await api.post('/admin/plans', planData);
        toast.success('Plan oluşturuldu');
      }
      
      setShowPlanDialog(false);
      setSelectedPlan(null);
      loadData();
    } catch (error) {
      toast.error('Plan kaydedilirken hata oluştu');
      console.error(error);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm('Bu planı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/admin/plans/${planId}`);
      toast.success('Plan silindi');
      loadData();
    } catch (error) {
      toast.error('Plan silinirken hata oluştu');
    }
  };

  const handleMovePlan = async (planId, direction) => {
    const planIndex = plans.findIndex(p => p.id === planId);
    if ((direction === 'up' && planIndex === 0) || (direction === 'down' && planIndex === plans.length - 1)) {
      return;
    }

    const newPlans = [...plans];
    const targetIndex = direction === 'up' ? planIndex - 1 : planIndex + 1;
    [newPlans[planIndex], newPlans[targetIndex]] = [newPlans[targetIndex], newPlans[planIndex]];

    // Update order for all plans
    for (let i = 0; i < newPlans.length; i++) {
      try {
        await api.put(`/admin/plans/${newPlans[i].id}`, { order: i });
      } catch (error) {
        console.error('Order update error:', error);
      }
    }

    loadData();
  };

  // Credit Management Functions
  const [approvingPurchaseId, setApprovingPurchaseId] = useState(null);
  const [rejectingPurchaseId, setRejectingPurchaseId] = useState(null);
  
  const handleApproveCreditPurchase = async (purchaseId) => {
    if (approvingPurchaseId) return; // Prevent double click
    
    try {
      setApprovingPurchaseId(purchaseId);
      const response = await api.post(`/admin/credits/approve/${purchaseId}`);
      toast.success(response.data.message || 'Kredi talebi onaylandı');
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Onaylama hatası';
      toast.error(errorMsg);
      console.error('Approve error:', error);
    } finally {
      setApprovingPurchaseId(null);
    }
  };

  const handleRejectCreditPurchase = async (purchaseId) => {
    if (rejectingPurchaseId) return; // Prevent double click
    
    if (!window.confirm('Kredi talebini reddetmek istediğinize emin misiniz?')) {
      return;
    }
    
    try {
      setRejectingPurchaseId(purchaseId);
      const response = await api.post(`/admin/credits/reject/${purchaseId}`);
      toast.success(response.data.message || 'Kredi talebi reddedildi');
      loadData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Reddetme hatası';
      toast.error(errorMsg);
      console.error('Reject error:', error);
    } finally {
      setRejectingPurchaseId(null);
    }
  };

  const handleAddCredits = () => {
    if (!selectedTenant) return;
    
    setCreditForm({
      tenant_id: selectedTenant.id,
      credits: '',
      is_bonus: true,
      note: ''
    });
    setShowCreditDialog(true);
  };

  const handleSubmitCredits = async () => {
    try {
      await api.post('/admin/credits/add', {
        tenant_id: creditForm.tenant_id,
        credits: parseInt(creditForm.credits),
        is_bonus: creditForm.is_bonus,
        note: creditForm.note
      });
      
      toast.success('Kredi eklendi');
      setShowCreditDialog(false);
      loadData();
    } catch (error) {
      toast.error('Kredi eklenirken hata oluştu');
      console.error(error);
    }
  };

  const handleViewTokenUsage = async (tenant) => {
    setSelectedTenant(tenant);
    setShowTokenDialog(true);
    setLoadingTokens(true);
    
    try {
      const response = await api.get(`/admin/tenants/${tenant.id}/token-usage`);
      setTokenUsageData(response.data);
    } catch (error) {
      toast.error('Token geçmişi yüklenirken hata oluştu');
      console.error(error);
    } finally {
      setLoadingTokens(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      trial: { className: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Deneme' },
      active: { className: 'bg-green-50 text-green-700 border-green-200', label: 'Aktif' },
      expired: { className: 'bg-red-50 text-red-700 border-red-200', label: 'Süresi Dolmuş' },
      canceled: { className: 'bg-gray-50 text-gray-700 border-gray-200', label: 'İptal' }
    };

    const variant = variants[status] || variants.trial;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Admin Panel</h1>
          <p className="text-slate-600">Kullanıcı ve plan yönetimi</p>
        </div>

        <Tabs defaultValue="tenants" className="space-y-6">
          <TabsList>
            <TabsTrigger value="tenants" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Tenantlar
            </TabsTrigger>
            <TabsTrigger value="credits" className="flex items-center gap-2">
              <Gift className="h-4 w-4" />
              Kredi Yönetimi
            </TabsTrigger>
            <TabsTrigger value="packages" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Kredi Paketleri
            </TabsTrigger>
            <TabsTrigger value="content" className="flex items-center gap-2">
              <Edit className="h-4 w-4" />
              Site İçeriği
            </TabsTrigger>
          </TabsList>

          {/* Tenants Tab */}
          <TabsContent value="tenants">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Tenantlar ({tenants.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Şirket</TableHead>
                        <TableHead>Kredi</TableHead>
                        <TableHead>Bonus</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Mağaza</TableHead>
                        <TableHead>İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.id}>
                          <TableCell className="font-medium">{tenant.email}</TableCell>
                          <TableCell>{tenant.company_name}</TableCell>
                          <TableCell>
                            <div className="font-bold text-[#FF6000]">{tenant.credits || 0}</div>
                          </TableCell>
                          <TableCell>
                            {tenant.bonus_credits > 0 ? (
                              <Badge className="bg-amber-50 text-amber-700 border-amber-200">
                                <Gift className="h-3 w-3 mr-1" />
                                {tenant.bonus_credits}
                              </Badge>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(tenant.subscription?.status || 'active')}
                          </TableCell>
                          <TableCell>{tenant.stores_count || 0}</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditTenant(tenant)}
                              >
                                <Edit className="h-3 w-3 mr-1" />
                                Düzenle
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewTokenUsage(tenant)}
                                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                              >
                                <BarChart3 className="h-3 w-3 mr-1" />
                                Token
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Credits Tab */}
          <TabsContent value="credits">
            <div className="grid gap-6">
              {/* Credit Purchases/Requests */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="h-5 w-5" />
                    Kredi Talepleri ({creditPurchases.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {creditPurchases.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">Henüz kredi talebi yok</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Kullanıcı</TableHead>
                          <TableHead>Kredi</TableHead>
                          <TableHead>Tutar</TableHead>
                          <TableHead>Soru</TableHead>
                          <TableHead>Durum</TableHead>
                          <TableHead>Tarih</TableHead>
                          <TableHead>İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {creditPurchases.map((purchase) => (
                          <TableRow key={purchase._id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{purchase.tenant_email}</div>
                                <div className="text-xs text-slate-500">{purchase.tenant_name}</div>
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold">{purchase.credits}</TableCell>
                            <TableCell>{purchase.amount_tl} ₺</TableCell>
                            <TableCell>{Math.floor(purchase.credits / 10)}</TableCell>
                            <TableCell>
                              <Badge className={
                                purchase.status === 'pending_approval' 
                                  ? 'bg-orange-100 text-orange-700 border-orange-200' 
                                  : purchase.status === 'approved'
                                  ? 'bg-green-100 text-green-700 border-green-200'
                                  : 'bg-red-100 text-red-700 border-red-200'
                              }>
                                {purchase.status === 'pending_approval' ? 'Bekliyor' : purchase.status === 'approved' ? 'Onaylandı' : 'Reddedildi'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {new Date(purchase.created_at).toLocaleDateString('tr-TR')}
                            </TableCell>
                            <TableCell>
                              {purchase.status === 'pending_approval' && (
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleApproveCreditPurchase(purchase._id)}
                                    disabled={approvingPurchaseId === purchase._id || rejectingPurchaseId === purchase._id}
                                    className="bg-green-600 hover:bg-green-700"
                                  >
                                    {approvingPurchaseId === purchase._id ? 'Onaylanıyor...' : 'Onayla'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRejectCreditPurchase(purchase._id)}
                                    disabled={approvingPurchaseId === purchase._id || rejectingPurchaseId === purchase._id}
                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                  >
                                    {rejectingPurchaseId === purchase._id ? 'Reddediliyor...' : 'Reddet'}
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Manual Credit Addition */}
              <Card>
                <CardHeader>
                  <CardTitle>Manuel Kredi Ekle</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label>Kullanıcı Seç</Label>
                      <select
                        className="w-full px-3 py-2 border rounded-md"
                        value={selectedTenant?.id || ''}
                        onChange={(e) => {
                          const tenant = tenants.find(t => t.id === e.target.value);
                          setSelectedTenant(tenant);
                        }}
                      >
                        <option value="">Kullanıcı seçin</option>
                        {tenants.map(tenant => (
                          <option key={tenant.id} value={tenant.id}>
                            {tenant.email} - {tenant.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedTenant && (
                      <div className="bg-slate-50 p-4 rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-slate-600">Mevcut Kredi:</span>
                            <span className="font-bold ml-2">{selectedTenant.credits || 0}</span>
                          </div>
                          <div>
                            <span className="text-slate-600">Bonus Kredi:</span>
                            <span className="font-bold ml-2">{selectedTenant.bonus_credits || 0}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <Button
                      onClick={handleAddCredits}
                      disabled={!selectedTenant}
                      className="w-full bg-[#FF6000] hover:bg-orange-600"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Kredi Ekle
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Credit Packages Tab */}
          <TabsContent value="packages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Kredi Paketleri ({creditPackages.length})
                  </CardTitle>
                  <Button onClick={handleCreatePackage} className="bg-[#FF6000] hover:bg-orange-600">
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Paket
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Paket Adı</TableHead>
                      <TableHead>Kredi Miktarı</TableHead>
                      <TableHead>Fiyat</TableHead>
                      <TableHead>Birim Fiyat</TableHead>
                      <TableHead>Popüler</TableHead>
                      <TableHead>Durum</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {creditPackages.map((pkg) => (
                      <TableRow key={pkg.id}>
                        <TableCell className="font-medium">{pkg.name}</TableCell>
                        <TableCell>{pkg.credits} kredi</TableCell>
                        <TableCell className="font-semibold text-[#FF6000]">{pkg.price} ₺</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {(pkg.price / pkg.credits).toFixed(2)} ₺/kredi
                        </TableCell>
                        <TableCell>
                          {pkg.popular && (
                            <Badge className="bg-[#FF6000]">Popüler</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={pkg.active ? "default" : "secondary"}>
                            {pkg.active ? 'Aktif' : 'Pasif'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditPackage(pkg)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600"
                              onClick={() => handleDeletePackage(pkg.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Site Content Tab */}
          <TabsContent value="content">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Edit className="h-5 w-5" />
                  Site İçerik Yönetimi ({siteContents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {siteContents.length === 0 ? (
                  <div className="text-center py-8 text-slate-600">
                    <p>İçerik bulunamadı. Veriler yükleniyor...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {siteContents.map((content) => (
                    <Card key={content.id} className="border hover:shadow-md transition-shadow">
                      <CardHeader>
                        <CardTitle className="text-lg capitalize">{content.page}</CardTitle>
                        <p className="text-xs text-slate-500 mt-1">
                          {content.content && Object.keys(content.content).length > 0 
                            ? `${Object.keys(content.content).length} alan` 
                            : 'İçerik yok'}
                        </p>
                      </CardHeader>
                      <CardContent>
                        <Button
                          onClick={() => {
                            console.log('Button clicked for:', content.page, content);
                            handleEditContent(content);
                          }}
                          className="w-full bg-[#FF6000] hover:bg-orange-600"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Düzenle
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Plans Tab - DISABLED (Using credit system now) */}
          {/* <TabsContent value="plans">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Abonelik Planları ({plans.length})
                  </CardTitle>
                  <Button onClick={handleCreatePlan} className="bg-[#FF6000] hover:bg-orange-600">
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Plan
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {plans.map((plan, index) => (
                    <div
                      key={plan.id}
                      className="border rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-bold">{plan.name}</h3>
                            <Badge variant={plan.active ? "default" : "secondary"}>
                              {plan.active ? 'Aktif' : 'Pasif'}
                            </Badge>
                          </div>
                          <p className="text-2xl font-bold text-[#FF6000] mb-3">
                            {plan.price} {plan.currency} / {plan.period === 'monthly' ? 'Ay' : 'Yıl'}
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-slate-600">Soru Limiti:</span>
                              <span className="ml-2 font-medium">{plan.features.questions_per_month}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">AI Model:</span>
                              <span className="ml-2 font-medium">{plan.features.ai_model}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">Destek:</span>
                              <span className="ml-2 font-medium">{plan.features.support}</span>
                            </div>
                            <div>
                              <span className="text-slate-600">Sıra:</span>
                              <span className="ml-2 font-medium">{plan.order}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMovePlan(plan.id, 'up')}
                              disabled={index === 0}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleMovePlan(plan.id, 'down')}
                              disabled={index === plans.length - 1}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditPlan(plan)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Düzenle
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeletePlan(plan.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Sil
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent> */}
        </Tabs>
      </div>

      {/* Tenant Edit Dialog - Simplified for Credit System */}
      <Dialog open={showTenantDialog} onOpenChange={setShowTenantDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tenant Bilgileri</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <div>
                <span className="text-sm text-slate-600">Email:</span>
                <div className="font-medium">{selectedTenant?.email}</div>
              </div>
              <div>
                <span className="text-sm text-slate-600">İsim:</span>
                <div className="font-medium">{selectedTenant?.name}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Normal Kredi</Label>
                <Input
                  type="number"
                  min="0"
                  value={tenantForm.credits}
                  onChange={(e) => setTenantForm({...tenantForm, credits: e.target.value})}
                />
              </div>
              <div>
                <Label>Bonus Kredi</Label>
                <Input
                  type="number"
                  min="0"
                  value={tenantForm.bonus_credits}
                  onChange={(e) => setTenantForm({...tenantForm, bonus_credits: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Durum</Label>
              <select
                className="w-full px-3 py-2 border border-slate-200 rounded-md"
                value={tenantForm.status}
                onChange={(e) => setTenantForm({...tenantForm, status: e.target.value})}
              >
                <option value="active">Aktif</option>
                <option value="suspended">Askıda</option>
                <option value="canceled">İptal</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Askıda: AI çalışmaz, kredi tüketilmez
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                className="flex-1 bg-[#FF6000] hover:bg-orange-600 text-white"
                onClick={handleUpdateTenant}
              >
                Durumu Güncelle
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowTenantDialog(false)}
              >
                İptal
              </Button>
            </div>

            <div className="pt-2 border-t">
              <p className="text-sm text-slate-600 mb-2">Kredi eklemek için:</p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setShowTenantDialog(false);
                  // Switch to credits tab
                  document.querySelector('[value="credits"]')?.click();
                }}
              >
                Kredi Yönetimi'ne Git
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credit Add Dialog */}
      <Dialog open={showCreditDialog} onOpenChange={setShowCreditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manuel Kredi Ekle</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <div className="text-sm font-medium">{selectedTenant?.email}</div>
              <div className="text-xs text-slate-600">{selectedTenant?.name}</div>
            </div>

            <div>
              <Label>Kredi Miktarı *</Label>
              <Input
                type="number"
                min="1"
                placeholder="Örn: 500"
                value={creditForm.credits}
                onChange={(e) => setCreditForm({...creditForm, credits: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">
                {creditForm.credits && `${Math.floor(creditForm.credits / 10)} soru = ${(creditForm.credits * 0.5).toFixed(2)} ₺`}
              </p>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_bonus"
                checked={creditForm.is_bonus}
                onChange={(e) => setCreditForm({...creditForm, is_bonus: e.target.checked})}
              />
              <Label htmlFor="is_bonus" className="cursor-pointer">
                Bonus kredi olarak ekle (önce kullanılır)
              </Label>
            </div>

            <div>
              <Label>Not</Label>
              <Input
                placeholder="İsteğe bağlı not"
                value={creditForm.note}
                onChange={(e) => setCreditForm({...creditForm, note: e.target.value})}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                className="flex-1 bg-[#FF6000] hover:bg-orange-600"
                onClick={handleSubmitCredits}
                disabled={!creditForm.credits || creditForm.credits < 1}
              >
                Krediyi Ekle
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCreditDialog(false)}
              >
                İptal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Token Usage Dialog */}
      <Dialog open={showTokenDialog} onOpenChange={setShowTokenDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Token Kullanım Geçmişi</DialogTitle>
            <div className="text-sm text-slate-600">
              {selectedTenant?.email}
            </div>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {loadingTokens ? (
              <div className="text-center py-8">Yükleniyor...</div>
            ) : tokenUsageData ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-slate-600">Toplam Soru</div>
                      <div className="text-2xl font-bold">{tokenUsageData.total_questions}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-slate-600">Prompt Token</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {tokenUsageData.total_prompt_tokens.toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-slate-600">Completion Token</div>
                      <div className="text-2xl font-bold text-green-600">
                        {tokenUsageData.total_completion_tokens.toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="text-sm text-slate-600">Toplam Token</div>
                      <div className="text-2xl font-bold text-[#FF6000]">
                        {tokenUsageData.total_tokens.toLocaleString()}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Questions List */}
                <div className="border rounded-lg">
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Ürün</TableHead>
                          <TableHead>Soru</TableHead>
                          <TableHead>Prompt</TableHead>
                          <TableHead>Completion</TableHead>
                          <TableHead>Toplam</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tokenUsageData.questions.map((q) => (
                          <TableRow key={q._id}>
                            <TableCell className="text-xs">
                              {new Date(q.created_at).toLocaleDateString('tr-TR')}
                            </TableCell>
                            <TableCell className="text-sm max-w-xs truncate">
                              {q.product?.title || 'N/A'}
                            </TableCell>
                            <TableCell className="text-sm max-w-xs truncate">
                              {q.customer_question}
                            </TableCell>
                            <TableCell className="text-sm text-blue-600">
                              {q.token_usage?.prompt_tokens || 0}
                            </TableCell>
                            <TableCell className="text-sm text-green-600">
                              {q.token_usage?.completion_tokens || 0}
                            </TableCell>
                            <TableCell className="text-sm font-bold">
                              {q.token_usage?.total_tokens || 0}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                Token verisi yok
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Plan Edit Dialog */}
      <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedPlan ? 'Planı Düzenle' : 'Yeni Plan Oluştur'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Plan Adı *</Label>
                <Input
                  placeholder="Başlangıç"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                />
              </div>

              <div>
                <Label>Fiyat *</Label>
                <Input
                  type="number"
                  placeholder="299"
                  value={planForm.price}
                  onChange={(e) => setPlanForm({...planForm, price: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Para Birimi</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  value={planForm.currency}
                  onChange={(e) => setPlanForm({...planForm, currency: e.target.value})}
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>

              <div>
                <Label>Periyot</Label>
                <select
                  className="w-full px-3 py-2 border border-slate-200 rounded-md"
                  value={planForm.period}
                  onChange={(e) => setPlanForm({...planForm, period: e.target.value})}
                >
                  <option value="monthly">Aylık</option>
                  <option value="yearly">Yıllık</option>
                </select>
              </div>

              <div>
                <Label>Sıra</Label>
                <Input
                  type="number"
                  value={planForm.order}
                  onChange={(e) => setPlanForm({...planForm, order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={planForm.active}
                  onChange={(e) => setPlanForm({...planForm, active: e.target.checked})}
                />
                Plan Aktif
              </Label>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Özellikler</h3>
              
              <div className="space-y-3">
                <div>
                  <Label>Aylık Soru Limiti *</Label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={planForm.features.questions_per_month}
                    onChange={(e) => setPlanForm({
                      ...planForm,
                      features: {...planForm.features, questions_per_month: e.target.value}
                    })}
                  />
                </div>

                <div>
                  <Label>AI Model</Label>
                  <Input
                    placeholder="Merkur AI"
                    value={planForm.features.ai_model}
                    onChange={(e) => setPlanForm({
                      ...planForm,
                      features: {...planForm.features, ai_model: e.target.value}
                    })}
                  />
                </div>

                <div>
                  <Label>Destek Türü</Label>
                  <Input
                    placeholder="Email Destek"
                    value={planForm.features.support}
                    onChange={(e) => setPlanForm({
                      ...planForm,
                      features: {...planForm.features, support: e.target.value}
                    })}
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={planForm.features.auto_mode}
                      onChange={(e) => setPlanForm({
                        ...planForm,
                        features: {...planForm.features, auto_mode: e.target.checked}
                      })}
                    />
                    Otomatik Mod Desteği
                  </Label>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                className="flex-1 bg-[#FF6000] hover:bg-orange-600 text-white"
                onClick={handleSavePlan}
              >
                {selectedPlan ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowPlanDialog(false)}
              >
                İptal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedPackage ? 'Paket Düzenle' : 'Yeni Paket Oluştur'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Paket Adı</label>
              <Input
                value={packageForm.name}
                onChange={(e) => setPackageForm({...packageForm, name: e.target.value})}
                placeholder="örn: Başlangıç Paketi"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Kredi Miktarı</label>
                <Input
                  type="number"
                  value={packageForm.credits}
                  onChange={(e) => setPackageForm({...packageForm, credits: parseInt(e.target.value) || ''})}
                  placeholder="100"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Fiyat (₺)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={packageForm.price}
                  onChange={(e) => setPackageForm({...packageForm, price: parseFloat(e.target.value) || ''})}
                  placeholder="50"
                />
              </div>
            </div>

            {packageForm.credits && packageForm.price && (
              <div className="bg-slate-50 p-3 rounded-lg">
                <div className="text-sm text-slate-600">Birim Fiyat:</div>
                <div className="text-lg font-semibold text-[#FF6000]">
                  {(packageForm.price / packageForm.credits).toFixed(2)} ₺/kredi
                </div>
              </div>
            )}

            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={packageForm.popular}
                  onChange={(e) => setPackageForm({...packageForm, popular: e.target.checked})}
                  className="w-4 h-4"
                />
                <span className="text-sm">Popüler Paket</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={packageForm.active}
                  onChange={(e) => setPackageForm({...packageForm, active: e.target.checked})}
                  className="w-4 h-4"
                />
                <span className="text-sm">Aktif</span>
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleSavePackage}
                className="flex-1 bg-[#FF6000] hover:bg-orange-600"
                disabled={!packageForm.name || !packageForm.credits || !packageForm.price}
              >
                {selectedPackage ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPackageDialog(false)}
                className="flex-1"
              >
                İptal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Content Edit Dialog */}
      <Dialog open={selectedPage !== null} onOpenChange={() => setSelectedPage(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {selectedPage} Sayfası İçeriğini Düzenle
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
              ℹ️ İçeriği JSON formatında düzenleyebilirsiniz. Değişiklikler anında sitede görünecektir.
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">İçerik (JSON Format)</label>
              <textarea
                className="w-full h-96 p-3 border rounded-lg font-mono text-sm"
                value={JSON.stringify(contentForm, null, 2)}
                onChange={(e) => {
                  try {
                    setContentForm(JSON.parse(e.target.value));
                  } catch (err) {
                    // Invalid JSON, don't update
                  }
                }}
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleSaveContent}
                className="flex-1 bg-[#FF6000] hover:bg-orange-600"
              >
                Kaydet
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedPage(null)}
                className="flex-1"
              >
                İptal
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
